import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import PrivacyChip from '../components/PrivacyChip';
import TimingHelper from '../components/TimingHelper';
import { fetchFlightStatus } from '../services/aviationApi';
import { sendTripShareEmail } from '../services/emailService';
import { verifySacramentoRegion } from '../services/geoService';

export default function ShareTrip() {
  const { user, grantXp, addNotification } = useApp();
  const [view, setView] = useState('traveler');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [activeTrip, setActiveTrip] = useState(null);
  const [sharedTrips, setSharedTrips] = useState([]);
  const [tripData, setTripData] = useState({ airline: '', flightNumber: '', terminal: 'Terminal B', companion: '', meetupSpot: 'Door 3 - Red Columns', meetupNote: '' });

  useEffect(() => {
    if (view === 'companion' && user.isLoggedIn) {
      fetchSharedWithMe();
    }
  }, [view, user.isLoggedIn]);

  const fetchSharedWithMe = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('trips_v2').select('*').eq('status', 'active');
    if (!error) setSharedTrips(data || []);
    setLoading(false);
  };

  const handleStartShare = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Step 1: Regional Check (30 miles from Sacramento Center)
      const { verified, distance } = await verifySacramentoRegion(30);
      
      if (!verified) {
        addNotification(`This service is currently restricted to the Sacramento region. (You are ${distance.toFixed(0)} miles away)`, 'error');
        setLoading(false);
        return;
      }

      const status = await fetchFlightStatus(tripData.flightNumber);
      const departureTime = status?.departure?.scheduled || new Date().toISOString();
      const arrivalTime = status?.arrival?.scheduled || new Date(Date.now() + 7200000).toISOString();
      
      if (user.isLoggedIn) {
        const { data: trip } = await supabase.from('trips_v2').insert([{ user_id: user.id, airline: tripData.airline, flight_number: tripData.flightNumber, terminal: tripData.terminal, meetup_spot: tripData.meetupSpot, meetup_note: tripData.meetupNote, scheduled_departure: departureTime, scheduled_arrival: arrivalTime }]).select().single();
        if (trip) {
          await supabase.from('trip_shares_v2').insert([{ trip_id: trip.id, sender_id: user.id, receiver_email: tripData.companion }]);
        }
      }

      await sendTripShareEmail({ sender_name: user.username, receiver_email: tripData.companion, airline: tripData.airline, flight_number: tripData.flightNumber, meetup_spot: tripData.meetupSpot, arrival_time: arrivalTime });
      
      setActiveTrip(status || { flight: { iata: tripData.flightNumber }, airline: { name: tripData.airline }, departure: { scheduled: departureTime }, arrival: { scheduled: arrivalTime }, flight_status: 'active' });
      setStep(2);
      grantXp(40, 'Trip shared! Connection verified in Sacramento.');
    } catch (err) {
      addNotification(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 pb-24 md:pb-8 max-w-lg mx-auto space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-white">Share My Trip</h2>
          <div className="flex space-x-4 mt-1">
            <button onClick={() => setView('traveler')} className={`text-xs font-bold uppercase tracking-wider ${view === 'traveler' ? 'text-amber-400 border-b-2 border-amber-400' : 'text-slate-500'}`}>I'm Flying</button>
            <button onClick={() => setView('companion')} className={`text-xs font-bold uppercase tracking-wider ${view === 'companion' ? 'text-amber-400 border-b-2 border-amber-400' : 'text-slate-500'}`}>Picking Someone Up</button>
          </div>
        </div>
        <PrivacyChip />
      </div>

      {view === 'traveler' ? (
        step === 1 ? (
          <form onSubmit={handleStartShare} className="space-y-4">
            <div className="bg-slate-850 p-5 rounded-2xl border border-slate-800 space-y-4">
              <div className="flex items-center space-x-2 text-[10px] text-emerald-400 font-bold uppercase bg-emerald-400/10 p-2 rounded-lg justify-center">
                <SafeIcon icon={FiIcons.FiCheckCircle} />
                <span>Sacramento Service Area Active</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase">Airline</label>
                  <input required type="text" placeholder="e.g. Southwest" className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white text-sm outline-none" value={tripData.airline} onChange={e => setTripData({ ...tripData, airline: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase">Flight #</label>
                  <input required type="text" placeholder="WN1234" className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white text-sm outline-none" value={tripData.flightNumber} onChange={e => setTripData({ ...tripData, flightNumber: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Companion Email</label>
                <input required type="email" placeholder="companion@example.com" className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white text-sm outline-none" value={tripData.companion} onChange={e => setTripData({ ...tripData, companion: e.target.value })} />
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-4 rounded-xl shadow-lg transition-all flex items-center justify-center space-x-2">
              {loading ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> : <SafeIcon icon={FiIcons.FiShare2} />}
              <span>Verify Region & Share (+40 XP)</span>
            </button>
          </form>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="bg-slate-850 border-2 border-amber-500/30 rounded-2xl p-5 shadow-xl">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <span className="text-[10px] bg-amber-500 text-slate-900 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">Notification Sent</span>
                  <h3 className="text-xl font-bold text-white mt-2">{activeTrip.airline.name} {activeTrip.flight.iata}</h3>
                </div>
                <p className="text-emerald-400 font-bold uppercase text-xs tracking-wider">Live</p>
              </div>
              <TimingHelper flightTime={activeTrip.arrival.scheduled} bagWait={20} />
              <button onClick={() => setStep(1)} className="w-full mt-6 bg-rose-500/10 border border-rose-500/20 text-rose-400 font-bold py-3 rounded-xl hover:bg-rose-500/20 transition-all text-sm">Stop Sharing</button>
            </div>
          </motion.div>
        )
      ) : (
        <div className="space-y-4">
          {sharedTrips.length === 0 && !loading ? (
            <div className="text-center py-12 bg-slate-850 rounded-2xl border border-slate-800">
              <SafeIcon icon={FiIcons.FiInbox} className="text-4xl text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">No trips shared with you yet.</p>
            </div>
          ) : (
            sharedTrips.map(trip => (
              <div key={trip.id} className="bg-slate-850 border border-slate-800 rounded-2xl p-5">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiIcons.FiUser} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold">{trip.flight_number}</h3>
                      <p className="text-slate-500 text-[10px] uppercase font-bold">{trip.airline}</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-emerald-400 font-bold uppercase py-0.5 px-2 bg-emerald-400/10 rounded">Live</span>
                </div>
                <TimingHelper flightTime={trip.scheduled_arrival} bagWait={20} isCompanion={true} />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}